import { Flex, Text } from '@sendoutcards/quantum-design-ui'
import React, { FC, useLayoutEffect, useRef } from 'react'
import { FeatureItem, FeatureItemProps } from './FeatureItem'
import { useSelector, useState } from 'src/hooks'

type FeatureColumn = {
  title?: string
  features: FeatureItemProps[]
}

type FeatureChartProps = {
  columns: FeatureColumn[] // primary columns; when using expandableColumn, pass your main column(s) here (usually 1)
  expandableColumn?: FeatureColumn & {
    defaultExpanded?: boolean
    collapsedPreviewCount?: number // how many items to show when collapsed
    collapsedLabel?: string
    expandedLabel?: string
  }
}

const ChevronIcon: FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-hidden="true"
  >
    <polyline
      points="8 4 16 12 8 20"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const FeatureItemChart: FC<FeatureChartProps> = ({
  columns,
  expandableColumn,
}) => {
  const toggleRef = useRef<HTMLDivElement | null>(null)
  const prevTopRef = useRef<number | null>(null)
  const pendingRef = useRef(false)
  const windowWidth = useSelector(state => state.window.width)
  const isMobile = windowWidth <= 1000
  const hasExpandable = !!expandableColumn
  const [isExpanded, setIsExpanded] = useState(
    expandableColumn?.defaultExpanded ?? false,
  )
  const previewCount = expandableColumn?.collapsedPreviewCount ?? 4
  const expandableCount = expandableColumn?.features?.length ?? 0
  const hasAnyExpandable = hasExpandable && expandableCount > 0
  const showToggle = hasAnyExpandable && expandableCount > previewCount
  const displayedExpandableFeatures =
    hasAnyExpandable && (isExpanded || expandableCount <= previewCount)
      ? expandableColumn!.features
      : []
  const showRightColumn =
    hasAnyExpandable && displayedExpandableFeatures.length > 0
  useLayoutEffect(() => {
    if (!pendingRef.current) return
    pendingRef.current = false
    if (typeof window === 'undefined') return
    requestAnimationFrame(() => {
      const n = toggleRef.current
      if (!n || prevTopRef.current == null) return
      const newTop = n.getBoundingClientRect().top
      const delta = newTop - prevTopRef.current
      if (Math.abs(delta) > 0.5) {
        window.scrollBy({ top: delta, left: 0 })
      }
      prevTopRef.current = null
    })
  }, [isExpanded])
  const handleToggle = () => {
    prevTopRef.current = toggleRef.current?.getBoundingClientRect().top ?? null
    pendingRef.current = true
    setIsExpanded(prev => !prev)
  }

  const expandableTitle =
    expandableColumn?.title && expandableColumn.title.trim() !== ''
      ? expandableColumn.title
      : 'Membership Features'

  return (
    <Flex
      flexDirection={isMobile ? 'column' : 'row'}
      flexWrap={isMobile ? 'wrap' : 'nowrap'}
      justifyContent={isMobile ? 'center' : 'space-between'}
      alignItems="stretch"
      inset={{ horizontal: 'x2_5', vertical: 'x3' }}
      backgroundColor="rgba(184, 184, 184, 0.14)"
      borderRadius="medium"
      columnGap="x1"
      maxWidth="1300px"
      style={{ position: 'relative' }}
    >
      {showToggle && (
        <div
          style={{
            position: 'absolute',
            top: isMobile ? 8 : 12,
            right: isMobile ? 8 : 12,
            zIndex: 2,
            pointerEvents: 'auto',
          }}
        >
          <div
            role="button"
            aria-label={
              isExpanded ? `Hide ${expandableTitle}` : `Show ${expandableTitle}`
            }
            title={
              isExpanded ? `Hide ${expandableTitle}` : `Show ${expandableTitle}`
            }
            onClick={handleToggle}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleToggle()
              }
            }}
            tabIndex={0}
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: 8,
              padding: '2px 10px',
              background: 'rgba(0,0,0,0.04)',
              borderRadius: 12,
              color: '#404040',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
            ref={toggleRef}
          >
            <Text type="caption">
              {isExpanded
                ? `Hide ${expandableTitle}`
                : `Show ${expandableTitle}`}
            </Text>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <ChevronIcon size={14} />
            </div>
          </div>
        </div>
      )}
      {(hasExpandable ? columns.slice(0, 1) : columns).map((col, colIndex) => {
        const hasFeatures = (col.features?.length ?? 0) > 0
        if (!hasFeatures) return null
        const inferredTitle =
          colIndex === 0 && (!col.title || col.title.trim() === '')
            ? 'Package Includes'
            : col.title
        return (
          <Flex
            key={colIndex}
            width={
              isMobile
                ? '100%'
                : (!hasExpandable && columns.length > 1) || showRightColumn
                  ? '50%'
                  : '100%'
            }
            flexDirection="column"
          >
            {inferredTitle && (
              <Text
                inset={{ left: '12px' }}
                type="title"
                style={{ fontSize: '14px', marginBottom: '8px' }}
              >
                {inferredTitle}
              </Text>
            )}
            {col.features.map((feature, i) => (
              <FeatureItem key={i} {...feature} />
            ))}
          </Flex>
        )
      })}
      {hasAnyExpandable && expandableColumn && showRightColumn && (
        <Flex
          width={isMobile ? '100%' : '50%'}
          flexDirection="column"
          style={{ position: 'relative' }}
        >
          {expandableColumn.title && (
            <Text
              inset={{ left: '12px' }}
              type="title"
              style={{ fontSize: '14px', marginBottom: '8px' }}
            >
              {expandableColumn.title}
            </Text>
          )}
          {displayedExpandableFeatures.map((feature, i) => (
            <FeatureItem key={i} {...feature} />
          ))}
        </Flex>
      )}
    </Flex>
  )
}
